import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { catchError, filter, switchMap, take, throwError } from 'rxjs';

import { AuthService } from '@services/auth/auth.service';
import { LOGIN_ROUTE } from '@shared/constants/routes.constants';

import { RefreshService } from './refresh.service';
import { RefreshStateService } from './refresh-state.service';

export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  const refreshService = inject(RefreshService);
  const router = inject(Router);
  const auth = inject(AuthService);
  const state = inject(RefreshStateService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      const isAuthEndpoint = req.url.includes('/api/auth/');
      if (err.status !== 401 || isAuthEndpoint) {
        return throwError(() => err);
      }

      if (state.isRefreshing) {
        return state.result$.pipe(
          filter((value): value is boolean => value !== null),
          take(1),
          switchMap((ok) =>
            ok
              ? next(req.clone({ withCredentials: true }))
              : throwError(() => err),
          ),
        );
      }

      state.startRefresh();

      return refreshService.refresh().pipe(
        switchMap(() => {
          state.completeRefresh(true);
          return next(req.clone({ withCredentials: true }));
        }),
        catchError((refreshErr) => {
          state.completeRefresh(false);

          // Solo expulsamos al login si había sesión: un 401 en una vista
          // pública (p. ej. el catálogo pidiendo config de llaveros) no debe
          // sacar de la tienda a un visitante anónimo.
          const hadSession = auth.isAuthenticated();
          auth.clear();

          if (hadSession) {
            router.navigateByUrl(LOGIN_ROUTE);
          }

          return throwError(() => refreshErr);
        }),
      );
    }),
  );
};
